'use client';

import { useState, useRef, useCallback } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';

export function useMapSync() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const mapRef = useRef<MapRef>(null);

  // Fly to a restaurant — zoom in to 16 minimum, but never zoom out from current level
  const panToRestaurant = useCallback((lat: number, lng: number) => {
    const currentZoom = mapRef.current?.getZoom() ?? 14;
    const targetZoom = Math.max(currentZoom, 16);
    mapRef.current?.flyTo({ center: [lng, lat], zoom: targetZoom, duration: 1000, essential: true });
  }, []);

  // Fit map bounds to show all visible markers
  const fitToMarkers = useCallback((coords: { lat: number; lng: number }[]) => {
    if (!mapRef.current || coords.length === 0) return;
    const map = mapRef.current;

    if (coords.length === 1) {
      map.flyTo({ center: [coords[0].lng, coords[0].lat], zoom: 15, duration: 800 });
      return;
    }

    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const c of coords) {
      if (c.lat < minLat) minLat = c.lat;
      if (c.lat > maxLat) maxLat = c.lat;
      if (c.lng < minLng) minLng = c.lng;
      if (c.lng > maxLng) maxLng = c.lng;
    }

    map.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      { padding: 60, duration: 800, maxZoom: 16 }
    );
  }, []);

  return { selectedId, setSelectedId, mapRef, panToRestaurant, fitToMarkers };
}
